import React, { useEffect, useMemo, useState } from "react";
import { Smartphone, Share, Plus, X, Download } from "lucide-react";

const DISMISS_KEY = "cc-install-dismissed-v2";

function isIOS() {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent || "");
}

function isInStandaloneMode() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  // iOS Safari
  return !!window.navigator.standalone;
}

/**
 * Floating install prompt — appears once per dismissal cycle on mobile,
 * iOS shows "Tap Share → Add to Home Screen" walkthrough,
 * Android fires the native beforeinstallprompt flow.
 */
export function InstallPrompt() {
  const [show, setShow] = useState(false);
  const [deferred, setDeferred] = useState(null);
  const ios = useMemo(() => isIOS(), []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isInStandaloneMode()) return;
    if (localStorage.getItem(DISMISS_KEY)) return;
    // Only show on mobile-class viewports
    const isMobile = window.matchMedia("(max-width: 820px)").matches;
    if (!isMobile) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };

    if (ios) {
      // No beforeinstallprompt on iOS — show the manual instructions after a beat
      const t = setTimeout(() => setShow(true), 2500);
      return () => clearTimeout(t);
    }

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, [ios]);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    setShow(false);
  };

  const installNow = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice.outcome === "accepted") {
        dismiss();
      }
    } catch {}
    setDeferred(null);
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[60] px-3 pb-3 sm:hidden animate-slideup"
      data-testid="install-prompt"
    >
      <div className="hud-frame bg-background/97 backdrop-blur-md border-[hsl(var(--oxblood))] shadow-2xl">
        <div className="tread-plate h-1" />
        <div className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 border border-[hsl(var(--oxblood))] flex items-center justify-center bg-[hsl(var(--surface))] flex-shrink-0">
              <Smartphone size={18} className="text-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="display font-bold text-base uppercase tracking-tight leading-tight">
                Install Command Center
              </div>
              <div className="mono text-[9px] uppercase tracking-[0.22em] text-muted-foreground mt-1">
                Full-screen · Home-screen icon · Offline-ready
              </div>
            </div>
            <button
              onClick={dismiss}
              className="text-muted-foreground hover:text-foreground -mt-1 -mr-1 p-1"
              data-testid="install-prompt-dismiss"
              aria-label="Dismiss"
            >
              <X size={16} />
            </button>
          </div>

          {ios ? (
            <div className="mt-4 space-y-2">
              <div className="flex items-center gap-2 text-xs">
                <span className="display font-bold text-[hsl(var(--oxblood))] w-4">1.</span>
                Tap the <Share size={13} className="inline mx-0.5 -mt-0.5" /> Share button in Safari
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="display font-bold text-[hsl(var(--oxblood))] w-4">2.</span>
                Scroll and tap <Plus size={13} className="inline mx-0.5 -mt-0.5" /> "Add to Home Screen"
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="display font-bold text-[hsl(var(--oxblood))] w-4">3.</span>
                Tap <span className="hud-chip hud-chip-on !text-[9px] mx-1">Add</span>
              </div>
            </div>
          ) : (
            <button
              onClick={installNow}
              className="hud-btn hud-btn-primary w-full justify-center mt-4"
              data-testid="install-prompt-cta"
            >
              <Download size={11} /> Install now
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Inline landing-page install hint — small bar that mentions installability.
 * Renders on mobile only, less aggressive than the floating sheet.
 */
export function LandingInstallHint() {
  const [hidden, setHidden] = useState(false);
  const ios = useMemo(() => isIOS(), []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isInStandaloneMode()) setHidden(true);
    if (localStorage.getItem(DISMISS_KEY)) setHidden(true);
  }, []);

  if (hidden) return null;

  return (
    <div
      className="md:hidden border-b border-border bg-[hsl(var(--surface))/0.5]"
      data-testid="landing-install-hint"
    >
      <div className="px-4 py-2.5 flex items-center gap-2 mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
        <Smartphone size={12} className="text-hud flex-shrink-0" />
        <span className="flex-1 truncate">
          {ios ? (
            <>Install: <span className="text-foreground">Share → Add to Home Screen</span></>
          ) : (
            <>Install · <span className="text-foreground">Tap menu → Install app</span></>
          )}
        </span>
      </div>
    </div>
  );
}
